/**
 * The proposal ledger (§9): open, revise, withdraw, decide (approve · reject · request revision · answer · adopt)
 * and expire. Approval binds revision + content hash; applying re-runs decide against the current state.
 */
import {
  ALLOW_MAX,
  type AllowRule,
  allowRulesFor,
  mcpToolCovered,
  REVIEW_CARD_DEADLINE_MS,
  rulesCover,
  withAllowEntries,
} from "@minga/protocol"
import {
  authorize,
  isMayor,
  managesProject,
  mayDecide,
  permissionMachineOf,
  permissionRefusal,
} from "../authorize"
import { chainApprover, duplicateNamed, isPersonOnlyCard, nextAddressee } from "../card-chain"
import { questionSubjectKey, sameSubjectCard } from "../card-subject"
import { contentHash, deriveId } from "../hash"
import { decidedFor, projectAdminsOf, routeCard } from "../multiplayer"
import { isApproveKey, MERGE_KEY, pickedArtifactId } from "../outputs"
import { capabilityVerdict, evaluatePolicy, exemptStamp } from "../policy"
import { policyProposalProblem, policyStaleProblem } from "../policy-card"
import { settingsProposalProblem } from "../project-settings"
import {
  AGENT_QUESTION_EFFECT_NAMES,
  failStage,
  holdForGate,
  isAgentQuestionEffect,
  isSystemEffect,
  openProposal,
  passStage,
  releasesGate,
  reviewIntentKey,
} from "../runs"
import {
  agentOf,
  artifactOf,
  isOpenProposal,
  isRunEnded,
  itemOf,
  leadOf,
  memberOf,
  must,
  principalOf,
  proposalOf,
  runOf,
} from "../state"
import type {
  Actor,
  ChangesetId,
  Command,
  GraphCommand,
  Ids,
  ItemId,
  OptionEffect,
  ProjectEvent,
  ProjectState,
  Proposal,
  ProposalBody,
  QuestionOption,
  UserId,
} from "../types"
import {
  applyChanges,
  askedOfChanges,
  outsideAuthority,
  proposePlanChange,
  submitChangeset,
} from "./changeset"
import { applyProjectWork } from "./coordination"
import { requestMerge } from "./github"
import { applyGraph, dependencyClosure } from "./graph"
import { decideFlow, setArtifactState } from "./items"
import { applyMemoryProposal, cardRecipientOf } from "./memory"
import { monitorToSet } from "./monitors"
import { adoptDocument, adoptWorkspaceDocument, onlyRetiresDrafts, pageProposalEffectProblem } from "./pages"
import { checkShareLoginPick } from "./share-login"
import { changeProjectSettings, checkProjectSettings, revisePolicy } from "./steering"
import { actorTaint, forbidden, invalid, isHuman, notFound, Rejected, reject, sameActor, Tx } from "./tx"

type Cmd<T extends Command["_tag"]> = Extract<Command, { _tag: T }>

/** Same proposer? Agents are compared by id (each turn is a different run). */
const isProposer = (actor: Actor, p: Proposal): boolean => {
  const by = p.proposedBy
  if (actor._tag === "agent" && by._tag === "agent") return actor.agentId === by.agentId
  if (actor._tag === "worker" && by._tag === "worker") return actor.itemId === by.itemId
  return sameActor(actor, by)
}

/** A question's option label by key ("answered" when there is none). */
const optionLabel = (p: Proposal, key: string | null): string =>
  (p.body._tag === "question" && key !== null
    ? p.body.options.find((o) => o.key === key)?.label
    : undefined) ??
  key ??
  "answered"

type QuestionAuthor = "system" | "agent" | "user"

const authorOf = (a: Actor): QuestionAuthor =>
  a._tag === "system" ? "system" : a._tag === "user" ? "user" : "agent"

const FLOW_TAGS: ReadonlySet<OptionEffect["_tag"]> = new Set([
  "RetryItem",
  "UnblockItem",
  "AcceptImpossible",
  "PauseItem",
  "SetArtifactState", // decision 0050: a pick's artifact states (never a graph command)
  "AdoptDocument", // decision 0084: a propose_page accept (never a graph command)
  "AdoptWorkspaceDocument", // decision 0086: a workspace page accept (delivered by the outbox; never a graph command)
  "ChangeProjectSettings", // decision 0119: a lead's settings card (UpdateProject as the person; never a graph command)
  "ShareLogin", // 0128 addendum: a login's owner shares it (delivered by the outbox; never a graph command)
  "RevisePolicy", // decision 0138: a chat grant's policy card (RevisePolicy as the person; never a graph command)
])

/** The graph commands among one option's effects, in order (decision 0032: dry-folded at open). */
const graphEffectsOf = (o: QuestionOption): ReadonlyArray<GraphCommand> =>
  o.effects.filter((e) => !FLOW_TAGS.has(e._tag)) as ReadonlyArray<GraphCommand>

/**
 * A question's options: unique keys; a system escalation offers flow effects only; an agent's question the agent
 * allowlist (decision 0032), then its graph effects are dry-folded as the agent (its `never` and the change's validity
 * apply at open time; flow effects are re-decided when a person answers).
 */
/** A card's explicit `to`: a member of the project who is not a viewer. */
const checkedTo = (s: ProjectState, to: UserId): UserId => {
  const m = memberOf(s, to)
  if (!m) invalid("to: not a member of this project", "to")
  if (m.role === "viewer") invalid(`to: @${m.handle} is a viewer and can't answer cards`, "to")
  return to
}

/**
 * Who answers a question (spec §2.4; decision 0126 §6, 0127): `to` (a member who is not a viewer); else, for an agent's
 * question, `routeCard`: the person responsible for the subject or the items it blocks, the run's asker, the project's
 * admins. A person's or the system's question: `owner`. Anyone in the project may still answer it (`mayDecide`).
 */
const questionAnswerer = (
  tx: Tx,
  to: UserId | undefined,
  body: Extract<ProposalBody, { _tag: "question" }>,
  blocks: ReadonlyArray<{ readonly _tag: string; readonly id: string }>,
  owner: UserId,
): UserId => {
  const s = tx.state
  if (to !== undefined) return checkedTo(s, to)
  if (tx.actor._tag !== "agent") return owner
  // decision 0126 §7: project settings are the project admins' (0119), whoever asked for them
  if (
    body.options.some((o) =>
      o.effects.some((e) => e._tag === "ChangeProjectSettings" || e._tag === "RevisePolicy"),
    )
  ) {
    // decision 0138: a policy card goes to the person who asked in this turn when they are an admin
    const a = tx.actor
    const asked = a._tag === "agent" ? runOf(s, a.runId)?.askedBy : undefined
    if (asked?._tag === "user" && managesProject(s, asked.userId)) return asked.userId
    return projectAdminsOf(s)[0] ?? owner
  }
  const items = [
    ...(body.subject?._tag === "item" ? [body.subject.id] : []),
    ...blocks.flatMap((b) => (b._tag === "item" ? [b.id as ItemId] : [])),
  ]
  return routeCard(s, tx.actor, { items }) ?? owner
}

const validateQuestion = (
  tx: Tx,
  body: Extract<ProposalBody, { _tag: "question" }>,
  author: QuestionAuthor,
) => {
  const keys = new Set<string>()
  body.options.forEach((o, i) => {
    if (keys.has(o.key)) invalid(`duplicate option key ${o.key}`, "body.options")
    keys.add(o.key)
    o.effects.forEach((e, j) => {
      // 0128 addendum: only Minga opens a share card (share-login.ts), never a question someone asks
      if (e._tag === "ShareLogin")
        invalid("only Minga asks to share a login", `body.options[${i}].effects[${j}]`)
      if (author === "system" && !isSystemEffect(e))
        invalid(`a system escalation may only offer flow effects, not ${e._tag}`, "body.options")
      // decision 0119: the project lead's settings card (propose_settings); applied as the person who answers
      if (e._tag === "ChangeProjectSettings") {
        if (author === "agent") {
          const why =
            tx.actor._tag === "agent" ? settingsProposalProblem(tx.state, tx.actor.agentId, e) : "agents only"
          if (why) invalid(why, `body.options[${i}].effects[${j}]`)
        }
        checkProjectSettings(tx, e.patch)
        return
      }
      // decision 0138: the project lead's policy card (propose_policy); applied as the person who answers
      if (e._tag === "RevisePolicy") {
        const why =
          author === "agent"
            ? tx.actor._tag === "agent"
              ? policyProposalProblem(tx.state, tx.actor.agentId, e)
              : "agents only"
            : policyStaleProblem(tx.state, e)
        if (why) invalid(why, `body.options[${i}].effects[${j}]`)
        return
      }
      // decision 0084 (§2.2): a propose_page card adopts or retires its own proposals/ draft
      const page = author === "agent" ? pageProposalEffectProblem(tx.state, e) : undefined
      if (typeof page === "string") invalid(page, `body.options[${i}].effects[${j}]`)
      if (author === "agent" && page === undefined && !isAgentQuestionEffect(e))
        invalid(
          `an agent's question may only offer ${AGENT_QUESTION_EFFECT_NAMES}, not ${e._tag}`,
          `body.options[${i}].effects[${j}]`,
        )
    })
  })
  if (body.defaultOnExpiry !== null && !keys.has(body.defaultOnExpiry))
    invalid("defaultOnExpiry must be an option key", "body.defaultOnExpiry")
  if (body.options.length === 0 && !body.allowFreeText)
    invalid("a question needs options or free text", "body.options")
  // each option on its own: a person picks one, so "Close as done" and "Cancel" on the same item both stay valid
  if (author === "agent")
    for (const o of body.options) {
      const graph = graphEffectsOf(o)
      if (graph.length > 0) dryFold(tx, graph)
    }
}

/**
 * Dry-fold a plan_change's changes as the proposer, to reject an invalid proposal up front (ghosts must render).
 * An agent may not propose what its policy says `never` to (same rule as SubmitChangeset{propose_all}, §7.2); the
 * Mayor has no project policy and only ever suggests to the lead, who re-files under its own.
 */
const dryFold = (tx: Tx, changes: ReadonlyArray<GraphCommand>) => {
  const scratch = new Tx(tx.state, tx.ctx)
  scratch.scope = { ...tx.scope }
  const checkPolicy = scratch.actor._tag !== "user" && !isMayor(scratch.state, scratch.actor)
  scratch.withScope({ changesetId: deriveId<ChangesetId>("chg", tx.now, "dry") }, () =>
    changes.forEach((change, index) => {
      if (checkPolicy) {
        const v = evaluatePolicy(scratch.state, scratch.actor, change)
        if (v._tag === "never")
          reject({
            _tag: "Forbidden",
            reason: "policy_never",
            detail: `change ${index} (${change._tag}): ${v.rule}`,
          })
      }
      try {
        scratch.withScope({ policy: exemptStamp(null) }, () => applyGraph(scratch, change))
      } catch (e) {
        if (e instanceof Rejected && e.rejection._tag === "Invalid")
          reject({ _tag: "Invalid", issues: e.rejection.issues.map((i) => ({ ...i, changeIndex: index })) })
        throw e
      }
    }),
  )
}

/**
 * Decision 0144: a card's `ask` (the question, one line) and `recommendation` (an option key, or approve / reject for
 * any other card, and why). Checked here so the proposing agent hears at once what is wrong.
 */
const checkCardShape = (c: Cmd<"OpenProposal">): void => {
  if (c.ask !== undefined && c.ask.trim() === "") invalid("ask is empty: write the question itself", "ask")
  const r = c.recommendation
  if (!r) return
  if (r.reason.trim() === "")
    invalid("recommendation.reason is empty: say why in one sentence", "recommendation")
  const keys: ReadonlyArray<string> =
    c.body._tag === "question" ? c.body.options.map((o) => o.key) : ["approve", "reject"]
  if (!keys.includes(r.option))
    invalid(
      `recommendation.option ${r.option} is not an answer of this card: one of ${keys.join(", ") || "(no options)"}`,
      "recommendation.option",
    )
}

export const openProposalCmd = (tx: Tx, c: Cmd<"OpenProposal">): void => {
  const actor = tx.actor
  const s = tx.state
  if (s.proposals.some((p) => p.id === c.proposalId)) invalid("proposal id already exists", "proposalId")
  checkCardShape(c)
  if (c.ask !== undefined || c.recommendation !== undefined)
    tx.cardShape = {
      proposalId: c.proposalId,
      ...(c.ask !== undefined ? { ask: c.ask.trim() } : {}),
      ...(c.recommendation !== undefined
        ? { recommendation: { option: c.recommendation.option, reason: c.recommendation.reason.trim() } }
        : {}),
    }
  const dup = s.proposals.find((p) => p.intentKey === c.intentKey && isOpenProposal(p))
  if (dup)
    reject({
      _tag: "Duplicate",
      existing: { _tag: "proposal", id: dup.id },
      reason: `${dup.ref} is already open for this intent`,
    })
  const mayor = isMayor(s, actor)
  const body = c.body
  if (mayor && body._tag !== "plan_change") forbidden("actor_kind", "the Mayor may only propose plan changes")
  if (actor._tag === "runner" && body._tag !== "permission")
    forbidden("actor_kind", "runners only relay permission prompts")
  switch (body._tag) {
    case "plan_change": {
      if (actor._tag === "runner") forbidden("actor_kind", "runners don't plan")
      dryFold(tx, body.changes)
      // decision 0127: `propose_plan{to}` names the person the card is for (the topic's person from the people pages)
      const to = c.to !== undefined ? checkedTo(s, c.to) : undefined
      proposePlanChange(
        tx,
        {
          changesetId: body.changesetId,
          rationale: body.rationale,
          revertOf: body.revertOf,
          undoOf: body.undoOf,
          questions: body.questions,
          containerId: body.containerId,
          to,
        },
        body.changes,
        c.proposalId,
        {
          reasoning: c.reasoning,
          oneLine: c.oneLine,
          risk: c.risk ?? "medium",
          reversibility: c.reversibility ?? "reversible",
          blocks: c.blocks,
          decideBy: c.decideBy ?? null,
          intentKey: c.intentKey,
        },
      )
      return
    }
    case "question": {
      if (actor._tag === "worker")
        forbidden("actor_kind", "workers ask their item's owner agent (ask), not people")
      validateQuestion(tx, body, authorOf(actor))
      const owner = tx.owner()
      const blocksActive = c.blocks.some(
        (b) => b._tag === "item" && itemOf(s, b.id)?.status === "in_progress",
      )
      const answerer = questionAnswerer(tx, c.to, body, c.blocks, owner)
      // decision 0093: one question per subject (item, options, gates) and person. The same agent's newer card replaces
      // its open one; another agent's open card, or one the person answered minutes ago, makes this one a duplicate
      const same = sameSubjectCard(
        s,
        actor,
        answerer,
        { kind: "question", key: questionSubjectKey(body, c.blocks) },
        tx.now,
      )
      if (same._tag === "duplicate") {
        const prev = same.proposal
        const answer = prev.resolution?.text?.trim() || optionLabel(prev, prev.resolution?.optionKey ?? null)
        reject({
          _tag: "Duplicate",
          existing: { _tag: "proposal", id: prev.id },
          reason: same.answered
            ? `${prev.ref} asked this ${Math.max(1, Math.round((tx.now - (prev.resolution?.at ?? tx.now)) / 60_000))} min ago and ${nameOf(s, prev.resolution?.by ?? actor)} answered "${answer}": act on that answer; if something new came up, ask with other options`
            : `${prev.ref} already asks this: leave it to its asker`,
        })
      }
      if (same._tag === "supersede") tx.emit({ _tag: "ProposalWithdrawn", proposalId: same.proposal.id })
      const tainted = actorTaint(s, actor) === "tainted"
      openProposal(tx, {
        id: c.proposalId,
        ownerId: answerer,
        body,
        proposedBy: actor,
        reasoning: c.reasoning,
        oneLine: c.oneLine,
        risk: c.risk ?? "low",
        reversibility: c.reversibility ?? "reversible",
        tier: blocksActive ? "blocking" : "soon",
        blocks: c.blocks,
        tainted,
        // decision 0102: an agent's question goes to the agent above it first; `answerer` is where it ends
        approver: chainApprover(s, actor, { _tag: "human", userId: answerer }, { body, blocks: c.blocks }),
        decideBy: c.decideBy ?? null,
        expiresAt: c.decideBy ?? null,
        intentKey: c.intentKey,
      })
      // decision 0041: a gate added after the item exists holds it: runs that have not started are cancelled
      const gated = c.blocks.flatMap((b) => (b._tag === "item" ? [b.id] : []))
      if (gated.length > 0)
        holdForGate(tx, gated, `held by ${must(proposalOf(tx.state, c.proposalId), "proposal").ref}`)
      if (body.answersAsk) {
        const ask = s.openAsks.find((a) => a.id === body.answersAsk)
        if (!ask) invalid("the ask is not open", "body.answersAsk")
        if (actor._tag !== "agent" || ask.ask?.to !== actor.agentId)
          forbidden("not_owner", "only the addressed agent escalates an ask")
        tx.emit({ _tag: "AskStateChanged", askId: ask.id, state: "escalated", by: c.proposalId })
      }
      return
    }
    case "permission": {
      const run = runOf(s, body.runId) ?? notFound({ _tag: "run", id: body.runId })
      if (run.state._tag === "ended") invalid("the run has ended", "body.runId")
      if (actor._tag === "agent" && run.agentId !== actor.agentId)
        forbidden("not_owner", "an agent relays permission prompts of its own turns only")
      openProposal(tx, {
        id: c.proposalId,
        ownerId: run.ownerId,
        body,
        proposedBy: actor,
        reasoning: c.reasoning,
        oneLine: c.oneLine,
        risk: c.risk ?? "medium",
        reversibility: c.reversibility ?? "costly",
        tier: "blocking",
        blocks: run.itemId ? [{ _tag: "run", id: run.id }] : [],
        tainted: run.taint === "tainted",
        approver: { _tag: "human", userId: run.ownerId },
        decideBy: null,
        // waits as long as needed and is withdrawn when the run ends; decision 0105: a reviewer's refusal waits at most
        // REVIEW_CARD_DEADLINE_MS, then it expires (= denied: the CLI keeps its own refusal)
        expiresAt: body.options?.review ? tx.now + REVIEW_CARD_DEADLINE_MS : null,
        intentKey: c.intentKey,
      })
      return
    }
    case "monitor": {
      // Explicit propose_monitor keeps its card semantics. Direct scheduling follows standing delegation.
      if (actor._tag !== "agent")
        forbidden("actor_kind", "an agent proposes a monitor; people set one (SetMonitor)")
      if (body.monitor.agentId !== actor.agentId)
        invalid("an agent proposes monitors for itself", "body.monitor.agentId")
      const to = cardRecipientOf(s, actor)
      const monitor = monitorToSet(s, body.monitor, to, "body.monitor")
      if (s.monitors?.some((m) => m.id === monitor.id))
        invalid("monitor id already exists", "body.monitor.id")
      const tainted = actorTaint(s, actor) === "tainted"
      const card = { _tag: "monitor" as const, monitor }
      openProposal(tx, {
        id: c.proposalId,
        ownerId: to,
        body: card,
        proposedBy: actor,
        reasoning: c.reasoning,
        oneLine: c.oneLine,
        risk: c.risk ?? "low",
        reversibility: c.reversibility ?? "reversible",
        tier: "soon",
        blocks: [],
        tainted,
        // decision 0106: the agent's lead adds her recommendation first (the 0102 chain); a person still decides it
        approver: chainApprover(s, actor, { _tag: "human", userId: to }, { body: card, blocks: [] }),
        decideBy: null,
        expiresAt: null,
        intentKey: c.intentKey,
      })
      return
    }
    case "outward_action":
    case "memory":
    case "project_request":
      invalid(
        body._tag === "outward_action"
          ? "outward actions are not proposed with propose_plan"
          : body._tag === "project_request"
            ? "project work is proposed with request_work"
            : "memory changes are proposed with remember",
        "body",
      )
  }
}

export const reviseProposal = (tx: Tx, c: Cmd<"ReviseProposal">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  if (!isProposer(tx.actor, p)) forbidden("not_owner", "only the proposer revises a proposal")
  if (p.state !== "open" && p.state !== "revision_requested")
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  if (c.revision !== p.revision)
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  if (c.body._tag !== p.body._tag) invalid("a revision keeps the proposal's kind", "body")
  if (c.body._tag === "plan_change") dryFold(tx, c.body.changes)
  if (c.body._tag === "question") validateQuestion(tx, c.body, authorOf(p.proposedBy))
  // Wave B2: new changes, new reasons. A plan card's footnote is judged again for the revised changes.
  const asked = c.body._tag === "plan_change" ? askedOfChanges(tx, c.body.changes) : undefined
  tx.emit({
    _tag: "ProposalRevised",
    proposalId: p.id,
    revision: p.revision + 1,
    contentHash: contentHash(c.body),
    body: c.body,
    reasoning: c.reasoning,
    ...(asked && asked.length > 0 ? { asked } : {}),
  })
}

export const withdrawProposal = (tx: Tx, c: Cmd<"WithdrawProposal">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  const human =
    tx.actor._tag === "user" && (p.ownerId === tx.actor.userId || mayDecide(tx.state, tx.actor, p) === null)
  if (!isProposer(tx.actor, p) && !human)
    forbidden("not_owner", "only the proposer or its approver withdraws a proposal")
  if (!isOpenProposal(p))
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  tx.emit({ _tag: "ProposalWithdrawn", proposalId: p.id })
}

/** Apply a list of option effects as `actor` (graph effects as changesets, flow effects directly). */
const applyEffects = (
  tx: Tx,
  effects: ReadonlyArray<OptionEffect>,
  actor: Actor,
  proposal: Proposal,
  seed: string,
) => {
  const graph: GraphCommand[] = []
  const flush = () => {
    if (graph.length === 0) return
    const changesetId = deriveId<ChangesetId>("chg", tx.now, `${proposal.id}:${seed}:${tx.events.length}`)
    tx.withScope({ actor, approvedBy: { proposalId: proposal.id, by: actor } }, () =>
      applyChanges(
        tx,
        { changesetId, rationale: `${proposal.ref}: ${seed}` },
        graph.map((change, index) => ({ change, index, stamp: exemptStamp("answer") })),
        proposal.id,
      ),
    )
    graph.length = 0
  }
  for (const e of effects) {
    if (actor._tag === "system" && !isSystemEffect(e))
      forbidden("system_cannot_create", `system effects can't ${e._tag}`)
    if (
      e._tag === "RetryItem" ||
      e._tag === "UnblockItem" ||
      e._tag === "AcceptImpossible" ||
      e._tag === "PauseItem"
    ) {
      flush()
      // a pause or resume that already holds is a no-op, not a failure of the whole answer (e.g. the resume ask_human
      // appends to a releasing option when someone resumed the item before the answer; decision 0041)
      if (e._tag === "PauseItem" && itemOf(tx.state, e.itemId)?.paused === e.paused) continue
      tx.withScope({ actor }, () => decideFlow(tx, e))
    } else if (e._tag === "SetArtifactState") {
      flush()
      const cur = artifactOf(tx.state, e.artifactId)?.state
      // idempotent: the person may have ✕'d it (or picked it) since the card opened (decision 0050)
      if (cur === e.state) continue
      // a ✕ stands: Approve all never turns a rejected output final (undo the ✕ first)
      if (e.state === "final" && cur === "rejected") continue
      tx.withScope({ actor }, () =>
        setArtifactState(tx, { _tag: "SetArtifactState", artifactId: e.artifactId, state: e.state }),
      )
    } else if (e._tag === "AdoptDocument") {
      flush()
      // decision 0084 §2.3: the draft's bytes become the page's next version, as the person who answered
      const id = deriveId<Ids["ArtifactId"]>("art", tx.now, `${proposal.id}:${seed}:adopt:${e.artifactId}`)
      tx.withScope({ actor }, () => adoptDocument(tx, e, id))
    } else if (e._tag === "AdoptWorkspaceDocument") {
      flush()
      // decision 0086 §4.2: checked as the person who answered; no project state changes (the outbox delivers it)
      tx.withScope({ actor }, () => adoptWorkspaceDocument(tx, e))
    } else if (e._tag === "ShareLogin") {
      flush()
      // 0128 addendum: checked at the pick (checkShareLoginPick); no project state changes (the outbox delivers it)
    } else if (e._tag === "ChangeProjectSettings") {
      flush()
      // decision 0119: as the person who answered (owner or admin: requireProjectManager), then the areas' WIP
      tx.withScope({ actor }, () => changeProjectSettings(tx, e))
      for (const a of e.areaWip ?? [])
        if (a.maxNow !== a.before)
          graph.push({ _tag: "UpdateArea", areaId: a.areaId, patch: { wip: { maxNow: a.maxNow } } })
      flush()
    } else if (e._tag === "RevisePolicy") {
      flush()
      // decision 0138: as the person who answered (a project admin: revisePolicy checks it and the revision), then the
      // open cards the new rules cover apply themselves
      tx.withScope({ actor }, () => {
        revisePolicy(tx, {
          _tag: "RevisePolicy",
          policyId: e.policyId,
          revision: e.revision,
          document: e.document,
        })
        closeCoveredPlanCards(tx, `covered by policy rev ${e.revision + 1}`)
      })
    } else graph.push(e as GraphCommand)
  }
  flush()
}

/** Run `f` in a child transaction; commit it into `tx` on success, return the rejection on failure. */
const attempt = (tx: Tx, f: (child: Tx) => void): Rejected | null => {
  const child = new Tx(tx.state, tx.ctx)
  child.scope = { ...tx.scope }
  try {
    f(child)
  } catch (e) {
    if (e instanceof Rejected) return e
    throw e
  }
  for (let i = 0; i < child.events.length; i++) tx.emit(child.events[i] as ProjectEvent, child.annotations[i])
  return null
}

/** The longest description the answers may grow (the external MCP's `MAX_BODY_CHARS`). */
const MAX_DECIDED_BODY = 40_000

export type PlanAnswers = ReadonlyArray<{ readonly key: string; readonly text: string }>

/**
 * Wave O1 (decision 0061, decide first): the "## Decided before start" section of a plan card's questions. A question
 * the person did not answer takes the proposer's recommendation, so a plain Approve means "use your recommendations".
 */
export const decidedSection = (
  questions: ReadonlyArray<{
    readonly key: string
    readonly question: string
    readonly recommendation: string
  }>,
  answers: PlanAnswers | undefined,
  proposerName: string,
  deciderName: string,
): string =>
  [
    "## Decided before start",
    ...questions.map((q) => {
      const own = answers?.find((a) => a.key === q.key)?.text.trim()
      const answer = own && own.length > 0 && own !== q.recommendation.trim() ? own : null
      return `- ${q.question} → ${answer ?? q.recommendation} (${answer ? `answered by ${deciderName}` : `${proposerName}'s recommendation`})`
    }),
  ].join("\n")

/** `body` with the section appended, cut to fit (the section loses its tail first). */
const withSection = (body: string, section: string): string => {
  const sep = body.trim().length > 0 ? "\n\n" : ""
  const room = MAX_DECIDED_BODY - body.length - sep.length
  if (room <= 0) return body
  return `${body}${sep}${section.length > room ? `${section.slice(0, Math.max(0, room - 1))}…` : section}`
}

const nameOf = (s: ProjectState, a: Actor): string => {
  if (a._tag === "user") return memberOf(s, a.userId)?.displayName ?? "a person"
  if (a._tag === "agent" || a._tag === "worker") return agentOf(s, a.agentId)?.name ?? "the agent"
  return "Minga"
}

/** Approve (all or a dependency-closed selection of) a plan_change: re-decided as the proposer, exempt. */
const approvePlanChange = (
  tx: Tx,
  p: Proposal,
  body: Extract<ProposalBody, { _tag: "plan_change" }>,
  selection: ReadonlyArray<number> | undefined,
  answers?: PlanAnswers,
  /** Decision 0138: the resolution's note when nothing else takes it ("covered by policy rev n"). */
  coveredNote?: string,
) => {
  const closure = selection
    ? dependencyClosure(body.changes, selection)
    : { ok: body.changes.map((_, i) => i) }
  if ("missing" in closure)
    invalid(`the selection also needs changes ${closure.missing.join(", ")}`, "decision.selection")
  if (closure.ok.length === 0) invalid("select at least one change", "decision.selection")
  const decider = tx.actor
  // decide first (decision 0061): where the answers land. A container created in the applied changes gets them in its
  // CreateItem body (one event); an existing one gets an UpdateItem as the decider; none → the resolution's note
  const questions = body.questions ?? []
  for (const a of answers ?? [])
    if (!questions.some((q) => q.key === a.key))
      invalid(`${a.key} is not a question of this card`, "decision.answers")
  const section =
    questions.length > 0
      ? decidedSection(questions, answers, nameOf(tx.state, p.proposedBy), nameOf(tx.state, decider))
      : null
  const createdAt = body.containerId
    ? closure.ok.find((i) => {
        const c = body.changes[i]
        return c?._tag === "CreateItem" && c.itemId === body.containerId
      })
    : undefined
  const createdElsewhere =
    body.containerId !== undefined &&
    createdAt === undefined &&
    body.changes.some((c) => c._tag === "CreateItem" && c.itemId === body.containerId)
  const existing = body.containerId && !createdElsewhere ? itemOf(tx.state, body.containerId) : undefined
  const target: "create" | "update" | "note" | null =
    section === null ? null : createdAt !== undefined ? "create" : existing ? "update" : "note"
  tx.emit({
    _tag: "ProposalApproved",
    proposalId: p.id,
    revision: p.revision,
    selection: selection ? [...closure.ok] : null,
    ...(target === "note" && section
      ? { note: coveredNote ? `${coveredNote}\n\n${section}` : section }
      : coveredNote
        ? { note: coveredNote }
        : {}),
    ...decidedFor(tx.state, p, decider),
  })
  const changeAt = (index: number): GraphCommand => {
    const c = must(body.changes[index], "change")
    if (target === "create" && section && index === createdAt && c._tag === "CreateItem")
      return { ...c, body: withSection(c.body ?? "", section) } as GraphCommand
    return c
  }
  const failed = attempt(tx, (child) => {
    child.withScope({ actor: p.proposedBy, approvedBy: { proposalId: p.id, by: decider } }, () =>
      applyChanges(
        child,
        {
          changesetId: body.changesetId,
          rationale: body.rationale,
          revertOf: body.revertOf,
          undoOf: body.undoOf,
        },
        closure.ok.map((index) => ({
          change: changeAt(index),
          index,
          stamp: exemptStamp("approved"),
        })),
        p.id,
      ),
    )
    const container =
      target === "update" && body.containerId ? itemOf(child.state, body.containerId) : undefined
    // best effort: answers that can't be written (a closed container) never fail the approved plan
    if (container && section)
      attempt(child, (grand) =>
        grand.withScope({ actor: decider, approvedBy: { proposalId: p.id, by: decider } }, () =>
          applyChanges(
            grand,
            {
              changesetId: deriveId<ChangesetId>("chg", child.now, `${p.id}:decided`),
              rationale: `${p.ref}: decided before start`,
            },
            [
              {
                change: {
                  _tag: "UpdateItem",
                  itemId: container.id,
                  patch: { body: withSection(container.body, section) },
                } as GraphCommand,
                index: 0,
                stamp: exemptStamp("approved"),
              },
            ],
            p.id,
          ),
        ),
      )
  })
  if (failed)
    tx.emit({
      _tag: "ProposalApplyFailed",
      proposalId: p.id,
      revision: p.revision,
      rejection: failed.rejection,
    })
  else
    tx.emit({
      _tag: "ProposalApplied",
      proposalId: p.id,
      revision: p.revision,
      changesetId: body.changesetId,
    })
}

/**
 * Decision 0050: an option that sets artifact states (a pick) is refused from a card that is out of date (not the
 * item's current round, or the item is not in review at the human stage), and a pick of an output that is rejected now.
 */
const guardPick = (
  tx: Tx,
  p: Proposal,
  body: Extract<ProposalBody, { _tag: "question" }>,
  opt: QuestionOption,
): void => {
  const sets = opt.effects.filter((e) => e._tag === "SetArtifactState")
  if (sets.length === 0) return
  // decision 0084 (§2.2): a propose_page card's accept or decline retires its draft; it is not a review pick
  if (onlyRetiresDrafts(tx.state, opt.effects)) return
  const t = body.subject?._tag === "item" ? itemOf(tx.state, body.subject.id) : undefined
  if (
    !t ||
    p.intentKey !== reviewIntentKey(t.id, t.review.round) ||
    t.status !== "in_review" ||
    t.review.stages[t.review.stage] !== "human"
  )
    invalid("this review card is out of date", "decision.optionKey")
  const finals = sets.filter((e) => e.state === "final")
  const picked = pickedArtifactId(opt.key) ?? (finals.length === 1 ? (finals[0]?.artifactId ?? null) : null)
  const x = picked ? artifactOf(tx.state, picked) : undefined
  if (x?.state === "rejected") invalid(`${x.title} was rejected: undo the ✕ first`, "decision.optionKey")
}

/** The item has a PR whose Merge from Minga is merging or queued (decision 0059). */
const mergeInFlight = (s: ProjectState, itemId: ItemId): boolean =>
  s.artifacts.some(
    (a) =>
      a.itemId === itemId &&
      a.kind === "pull_request" &&
      (a.merge?.state === "merging" || a.merge?.state === "queued"),
  )

/** A person answered a question: record it, then run the chosen option's effects as that person. */
const answerQuestion = (
  tx: Tx,
  p: Proposal,
  body: Extract<ProposalBody, { _tag: "question" }>,
  optionKey: string | null,
  text: string | null,
) => {
  const opt: QuestionOption | undefined =
    optionKey === null ? undefined : body.options.find((o) => o.key === optionKey)
  if (optionKey !== null && !opt) invalid(`no option ${optionKey}`, "decision.optionKey")
  if (optionKey === null && (!body.allowFreeText || !text))
    invalid("pick an option or answer in free text", "decision")
  // decision 0059: Merge on a human review card orders the merge; the card stays open until the result
  if (
    optionKey === MERGE_KEY &&
    body.subject?._tag === "item" &&
    p.intentKey.startsWith(`review:${body.subject.id}:`)
  ) {
    requestMerge(tx, p)
    return
  }
  // a merge order already went out (it cannot be recalled): until its result, only Approve is taken on the review card
  if (
    !isApproveKey(optionKey) &&
    body.subject?._tag === "item" &&
    p.intentKey.startsWith(`review:${body.subject.id}:`) &&
    mergeInFlight(tx.state, body.subject.id)
  )
    invalid("a merge is in flight; wait for its result", "decision.optionKey")
  if (opt) guardPick(tx, p, body, opt)
  // decision 0119: project settings are the owner's or an admin's; anyone else's pick leaves the card open
  if (opt?.effects.some((e) => e._tag === "ChangeProjectSettings")) {
    const owner = tx.state.project?.ownerId
    if (tx.actor._tag !== "user" || !owner || !managesProject(tx.state, tx.actor.userId))
      forbidden("not_owner", "project settings are applied by the project's admins")
  }
  // decision 0138: policies too
  if (opt?.effects.some((e) => e._tag === "RevisePolicy")) {
    if (tx.actor._tag !== "user" || !managesProject(tx.state, tx.actor.userId))
      forbidden("not_owner", "policies are changed by the project's admins")
  }
  // 0128 addendum: only the login's owner shares it; anyone else's pick leaves the card open
  if (opt) checkShareLoginPick(tx, opt.effects)
  tx.emit({
    _tag: "ProposalAnswered",
    proposalId: p.id,
    revision: p.revision,
    optionKey,
    text,
    ...decidedFor(tx.state, p, tx.actor),
  })
  const decider = tx.actor
  // decision 0158: a written answer about an item is feedback its next attempt reads; it never changes who the item
  // runs as
  const failed = attempt(tx, (child) => {
    // the human review stage of an item (§10.8)
    if (
      body.subject?._tag === "item" &&
      p.intentKey ===
        reviewIntentKey(body.subject.id, itemOf(child.state, body.subject.id)?.review.round ?? -1)
    ) {
      const t = itemOf(child.state, body.subject.id)
      if (t?.status === "in_review" && t.review.stages[t.review.stage] === "human") {
        child.emit({
          _tag: "ReviewRecorded",
          itemId: t.id,
          round: t.review.round,
          stage: "human",
          verdict: isApproveKey(optionKey) ? "approve" : "changes_requested",
          notes: text ?? "",
          findings: text && !isApproveKey(optionKey) ? [text] : [],
          runId: null,
        })
        // anything but "approve" or a pick "approve:<id>" (decision 0050) is changes_requested: the "changes" option,
        // another option or a free-text reply
        if (isApproveKey(optionKey)) passStage(child, t.id, "approve")
        else failStage(child, t.id, "changes_requested", null)
      }
    }
    if (opt) applyEffects(child, opt.effects, decider, p, opt.key)
    holdUnlessReleased(child, p, body, opt, text)
    if (body.answersAsk) {
      const ask = child.state.openAsks.find((a) => a.id === body.answersAsk)
      if (ask) child.emit({ _tag: "AskStateChanged", askId: ask.id, state: "answered", by: p.id })
    }
  })
  if (failed)
    tx.emit({
      _tag: "ProposalApplyFailed",
      proposalId: p.id,
      revision: p.revision,
      rejection: failed.rejection,
    })
}

/**
 * Only an explicit go-ahead releases a gate (decision 0041): a question that `blocks` items, answered with an option
 * that does not release (`releases ?? key === "approve"`) and carries no effects, or in free text, pauses the gated
 * items as the person who answered. Escalations (their options carry the flow effects) and escalated worker asks (the
 * answer is the information the worker waits for) are unaffected.
 */
const holdUnlessReleased = (
  tx: Tx,
  p: Proposal,
  body: Extract<ProposalBody, { _tag: "question" }>,
  opt: QuestionOption | undefined,
  text: string | null,
): void => {
  // escalations are flow cards; an escalated worker ask is answered in words (the answer unblocks the ask)
  if (body.escalation !== null || body.answersAsk !== null) return
  if (opt && (releasesGate(opt) || opt.effects.length > 0)) return
  const answered = (opt?.label ?? text ?? "").trim().slice(0, 80)
  const held: ItemId[] = []
  for (const b of p.blocks) {
    if (b._tag !== "item") continue
    const t = itemOf(tx.state, b.id)
    if (!t || t.status === "done" || t.status === "cancelled") continue
    held.push(t.id)
    if (t.paused) continue
    tx.emit({
      _tag: "ItemPaused",
      itemId: t.id,
      paused: true,
      by: principalOf(tx.actor),
      note: `held: answered "${answered}" on ${p.ref}`,
    })
  }
  if (held.length > 0) holdForGate(tx, held, `held: answered on ${p.ref}`)
}

/**
 * Decision 0096: "Always allow" on a permission card adds the CLI's proposed rules to the project's allowlist, as the
 * person who answered when they manage the project (the list applies to every run of the project). Decision 0127:
 * anyone else's "always" is refused before this (`permissionRefusal`). Rules already listed stay as they are.
 */
const rememberAllow = (tx: Tx, rules: ReadonlyArray<AllowRule>): void => {
  const project = tx.state.project
  const actor = tx.actor
  if (!project || actor._tag !== "user" || !managesProject(tx.state, actor.userId)) return
  const before = project.allow ?? []
  const next = withAllowEntries(before, rules, actor.userId, tx.now, "card")
  if (next.length === before.length || next.length > ALLOW_MAX) return
  tx.emit({
    _tag: "ProjectUpdated",
    patch: { allow: next } as never,
    before: { allow: project.allow ?? null } as never,
  })
  closeCoveredPermissions(tx, allowRulesFor(next, null), "covered by the project's allowed list")
}

// ---- decision 0138: covered cards close ----------------------------------------------------------------------------

/** The `mcp__<server>__<tool>` readings of a relayed tool key (a server name may itself contain `__`). */
const mcpReadings = (tool: string): ReadonlyArray<{ readonly server: string; readonly tool: string }> => {
  if (!tool.startsWith("mcp__")) return []
  const rest = tool.slice("mcp__".length)
  const out: Array<{ server: string; tool: string }> = []
  for (let i = rest.indexOf("__"); i > 0; i = rest.indexOf("__", i + 1))
    out.push({ server: rest.slice(0, i), tool: rest.slice(i + 2) })
  return out
}

/**
 * Decision 0138 (kody's "a grant records what it covers"): a permission card the rules now cover. The CLI's own
 * proposal (`options.allow`) names what the request needs; every rule of it must be covered. An MCP tool prompt is also
 * covered by an `mcpTool` rule for its tool. A card that names nothing coverable stays.
 */
export const permissionCovered = (p: Pick<Proposal, "body">, rules: ReadonlyArray<AllowRule>): boolean => {
  if (p.body._tag !== "permission" || rules.length === 0) return false
  if (rulesCover(rules, p.body.options?.allow ?? [])) return true
  return mcpReadings(p.body.tool).some((r) => mcpToolCovered(r.server, r.tool, rules))
}

/**
 * Decision 0138: answer Allow once every open permission card `rules` now cover (and `which` admits), as `tx.actor`
 * (whose rule it is), with `note` as the resolution ("covered by …"). The runner gets the answer as for any card.
 */
export const closeCoveredPermissions = (
  tx: Tx,
  rules: ReadonlyArray<AllowRule>,
  note: string,
  which: (p: Proposal) => boolean = () => true,
): number => {
  let n = 0
  for (const p of tx.state.proposals) {
    if (!isOpenProposal(p) || p.body._tag !== "permission" || !which(p) || !permissionCovered(p, rules))
      continue
    const run = runOf(tx.state, p.body.runId)
    if (!run || isRunEnded(run)) continue
    tx.emit({
      _tag: "ProposalApproved",
      proposalId: p.id,
      revision: p.revision,
      selection: null,
      note,
      ...decidedFor(tx.state, p, tx.actor),
    })
    tx.emit({
      _tag: "ProposalApplied",
      proposalId: p.id,
      revision: p.revision,
      changesetId: null,
      remember: "once",
    })
    n++
  }
  return n
}

/**
 * Decision 0138: after a policy revision, apply every open plan card an agent filed only because its policy said
 * propose, when each of its changes is now within the proposer's own policy (`outsideAuthority` as the proposer finds
 * nothing). Cards the agent chose to ask (`asked`) or that wait on someone's own things keep waiting. Applied as
 * `tx.actor` (the person who revised the policy), noted `note`; a card that no longer applies stays open.
 */
export const closeCoveredPlanCards = (tx: Tx, note: string): number => {
  let n = 0
  for (const p of tx.state.proposals) {
    if (!isOpenProposal(p) || p.body._tag !== "plan_change" || p.proposedBy._tag !== "agent") continue
    if (isMayor(tx.state, p.proposedBy)) continue
    if ((p.asked ?? []).length === 0 || (p.asked ?? []).some((a) => a.rule === "asked")) continue
    const body = p.body
    const probe = new Tx(tx.state, tx.ctx)
    probe.scope = { ...tx.scope, actor: p.proposedBy }
    if (outsideAuthority(probe, body.changes) !== null) continue
    const failed = attempt(tx, (child) => approvePlanChange(child, p, body, undefined, undefined, note))
    if (!failed) n++
  }
  return n
}

/** Decision 0138: a runner owner's grants changed; close the cards from their machine the rules now cover. */
export const applyRunnerGrants = (tx: Tx, c: Cmd<"ApplyRunnerGrants">): void => {
  const actor = tx.actor
  if (actor._tag !== "user") forbidden("actor_kind", "a runner's grants are its owner's")
  const name = memberOf(tx.state, actor.userId)?.displayName?.trim() || "the owner"
  closeCoveredPermissions(tx, c.rules, `covered by ${name}'s grants on this machine`, (p) => {
    if (p.body._tag !== "permission") return false
    const run = runOf(tx.state, p.body.runId)
    if (run?.executor?._tag !== "runner" || run.executor.runnerId !== c.runnerId) return false
    return permissionMachineOf(tx.state, p)?.ownerId === actor.userId
  })
}

/** Approve a proposal as it stands now (plan change, outward action, permission, memory). */
const approve = (
  tx: Tx,
  p: Proposal,
  selection: ReadonlyArray<number> | undefined,
  remember?: "once" | "run" | "always",
  answers?: PlanAnswers,
): void => {
  const body = p.body
  switch (body._tag) {
    case "plan_change":
      approvePlanChange(tx, p, body, selection, answers)
      return
    case "outward_action":
      // Decision 0074: approval is authority for a native worker, not evidence that the call executed.
      tx.emit({
        _tag: "ProposalApproved",
        proposalId: p.id,
        revision: p.revision,
        selection: null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      {
        const item = itemOf(tx.state, body.itemId)
        if (
          item &&
          item.status !== "done" &&
          item.status !== "cancelled" &&
          !item.activeRunId &&
          item.status !== "todo"
        )
          tx.emit({
            _tag: "ItemStatusChanged",
            itemId: item.id,
            from: item.status,
            to: "todo",
            cause: { _tag: "planned" },
            blocked: null,
            resolution: null,
          })
      }
      return
    case "permission":
      // Decision 0053: "always" = Codex's own exec-policy rule, only when the prompt offered one (its prefix stays on
      // the runner; the decision only says "always").
      // Decision 0096: or the allowlist rules the prompt offered (`options.allow`), added to the project's list.
      if (remember === "always" && !body.options?.alwaysPrefix?.length && !body.options?.allow?.length)
        invalid("this permission offers no rule to remember", "decision.remember")
      // decision 0126 §3 (0127): a lasting rule is the machine owner's (and the project's allowlist a manager's); it is
      // refused rather than dropped, so the log never says "always" for a rule nobody wrote
      if (remember === "always") {
        const why =
          tx.actor._tag === "user"
            ? permissionRefusal(tx.state, tx.actor.userId, p, "always")
            : "only a person sets an always rule"
        if (why) forbidden("not_owner", why)
      }
      tx.emit({
        _tag: "ProposalApproved",
        proposalId: p.id,
        revision: p.revision,
        selection: null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      // K13: "Allow for this run" is recorded on the applied event (ignored on other kinds)
      tx.emit({
        _tag: "ProposalApplied",
        proposalId: p.id,
        revision: p.revision,
        changesetId: null,
        ...(remember ? { remember } : {}),
      })
      // decision 0138 (0126 §3): on a machine the rules go to its owner's own list (the project's outbox delivers them to
      // the Workspace DO, which closes that machine's other cards they cover); the project's list stays for the team's
      // Platform Runner, where the project's admins stand in for an owner
      if (remember === "always" && body.options?.allow?.length && !permissionMachineOf(tx.state, p))
        rememberAllow(tx, body.options.allow)
      return
    case "memory": {
      tx.emit({
        _tag: "ProposalApproved",
        proposalId: p.id,
        revision: p.revision,
        selection: null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      const failed = attempt(tx, (child) => applyMemoryProposal(child, p, body))
      if (failed)
        tx.emit({
          _tag: "ProposalApplyFailed",
          proposalId: p.id,
          revision: p.revision,
          rejection: failed.rejection,
        })
      else tx.emit({ _tag: "ProposalApplied", proposalId: p.id, revision: p.revision, changesetId: null })
      return
    }
    case "monitor": {
      // wave C (decision 0071): Accept = SetMonitor as the person who decides (they become its setter)
      if (tx.actor._tag !== "user") forbidden("actor_kind", "a person accepts a monitor")
      const setter = tx.actor.userId
      tx.emit({
        _tag: "ProposalApproved",
        proposalId: p.id,
        revision: p.revision,
        selection: null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      const failed = attempt(tx, (child) =>
        child.emit({
          _tag: "MonitorSet",
          monitor: monitorToSet(child.state, body.monitor, setter, "body.monitor"),
        }),
      )
      if (failed)
        tx.emit({
          _tag: "ProposalApplyFailed",
          proposalId: p.id,
          revision: p.revision,
          rejection: failed.rejection,
        })
      else tx.emit({ _tag: "ProposalApplied", proposalId: p.id, revision: p.revision, changesetId: null })
      return
    }
    case "project_request": {
      if (tx.actor._tag !== "user") forbidden("actor_kind", "a person approves a project request")
      tx.emit({
        _tag: "ProposalApproved",
        proposalId: p.id,
        revision: p.revision,
        selection: null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      const failed = attempt(tx, (child) => applyProjectWork(child, body.draft, p.proposedBy, p.tainted))
      if (failed)
        tx.emit({
          _tag: "ProposalApplyFailed",
          proposalId: p.id,
          revision: p.revision,
          rejection: failed.rejection,
        })
      else tx.emit({ _tag: "ProposalApplied", proposalId: p.id, revision: p.revision, changesetId: null })
      return
    }
    case "question":
      invalid("answer a question with one of its options", "decision")
  }
}

/**
 * A person edits the proposal before approving it (Needs-you "Edit"). Same kind only; a permission prompt is
 * approved or denied as asked. The edit is logged as ProposalRevised by the approver; the approval binds to the
 * new revision and content hash. A plan change is still dry-folded, so an invalid edit is rejected up front.
 */
const approveEdited = (tx: Tx, p: Proposal, body: ProposalBody): void => {
  if (!isHuman(tx.actor)) forbidden("actor_kind", "only people edit a proposal while approving it")
  if (body._tag !== p.body._tag) invalid("an edit keeps the proposal's kind", "decision.body")
  if (body._tag === "permission" || body._tag === "question")
    invalid(
      `a ${body._tag} is ${body._tag === "question" ? "answered" : "approved or denied"} as asked`,
      "decision",
    )
  if (body._tag === "plan_change") dryFold(tx, body.changes)
  if (body._tag === "memory" && p.body._tag === "memory" && body.itemId !== p.body.itemId)
    invalid("an edit keeps the memory item", "decision.body.itemId")
  if (body._tag === "monitor" && p.body._tag === "monitor" && body.monitor.agentId !== p.body.monitor.agentId)
    invalid("an edit keeps the monitor's agent", "decision.body.monitor.agentId")
  tx.emit({
    _tag: "ProposalRevised",
    proposalId: p.id,
    revision: p.revision + 1,
    contentHash: contentHash(body),
    body,
    reasoning: p.reasoning,
  })
  approve(tx, must(proposalOf(tx.state, p.id), "proposal"), undefined)
}

/** The events that record a decision (the ones a relay names its agent on). */
const DECISION_TAGS: ReadonlySet<ProjectEvent["_tag"]> = new Set([
  "ProposalAnswered",
  "ProposalApproved",
  "ProposalRejected",
])

/**
 * Decision 0093: the person a relayed answer comes from, or a refusal. An agent applies a person's chat answer only in
 * a turn run of its own that this person started (`startedBy` person, `askedBy` the person; never a turn changes
 * started), and only for cards an agent may carry: not an outward action, a permission prompt or a project request,
 * and never Merge (an outward GitHub action). Those stay in the app. Decision 0134 (0133 §3): taint no longer blocks it.
 */
export const relayPersonOf = (
  s: ProjectState,
  actor: Actor,
  p: Proposal,
  decision: Cmd<"DecideProposal">["decision"],
  from?: RelayFrom,
): UserId => {
  if (actor._tag !== "agent" || isMayor(s, actor))
    forbidden("actor_kind", "only a project agent applies a person's chat answer")
  const run = runOf(s, actor.runId)
  if (run?.kind !== "turn" || run.agentId !== actor.agentId)
    forbidden("actor_kind", "a chat answer is applied in the turn that carries it")
  // decision 0142 (0140 #22): the person's words in a teammate agent's team chat, found there by the tool layer, are
  // enough from any of this agent's turns; the decision records that chat message as its evidence
  if (from) {
    if (!agentOf(s, from.agentId as never))
      invalid("the chat is not a team chat of this project", "relay.from")
    if (!s.members.some((m) => m.userId === from.userId))
      forbidden("actor_kind", "the quote is not from a member of this project")
  } else if (run.startedBy === "changes" || run.askedBy?._tag !== "user")
    forbidden(
      "actor_kind",
      "only in a turn a person started with a message: answer cards from a wake turn in the app, or ask in chat; or quote what the person said in a teammate's chat (resolve_card with chat and message)",
    )
  if (p.body._tag === "outward_action" || p.body._tag === "permission" || p.body._tag === "project_request")
    forbidden(
      "actor_kind",
      `${p.ref} is answered in the app: outward actions, permission prompts and project requests need the person's own click`,
    )
  if (decision._tag !== "answer" && decision._tag !== "approve" && decision._tag !== "reject")
    invalid("a chat answer approves, rejects or answers a card", "decision")
  if (decision._tag === "approve" && decision.remember !== undefined)
    invalid("a chat answer never remembers a permission", "decision.remember")
  if (decision._tag === "answer" && decision.optionKey === MERGE_KEY)
    forbidden("actor_kind", "Merge is the person's own click in the app")
  if (from) return from.userId as UserId
  if (run.askedBy?._tag !== "user") forbidden("actor_kind", "only in a turn a person started with a message")
  return run.askedBy.userId
}

/** Decision 0142: where a relayed quote came from when it is not the turn's own chat. */
type RelayFrom = { readonly agentId: string; readonly messageId: string; readonly userId: string }

/**
 * Decision 0116: "Jannik Jung (in chat), applied by Lea: "…"", for the card's resolution: who decided, where, and the
 * agent that only carried it.
 */
const relayNote = (s: ProjectState, person: Actor, agent: Actor, quote: string, from?: RelayFrom): string => {
  const peer = from ? agentOf(s, from.agentId as never) : undefined
  const own = agent._tag === "agent" && from?.agentId === agent.agentId
  const where = from && !own ? `in ${peer?.name ?? "a teammate"}'s chat` : "in chat"
  return `${nameOf(s, person)} (${where}), applied by ${nameOf(s, agent)}: "${quote.trim()}"`
}

/**
 * Decision 0093: decide as the person whose chat answer an agent applies. The decision runs exactly as if the person
 * had clicked it (their actor, their rights, the same effects and wakes); the decision event names the agent and run
 * that carried the answer (`relayedBy`) and quotes the person's words in its note.
 */
const relayDecision = (tx: Tx, c: Cmd<"DecideProposal">, p: Proposal, quote: string): void => {
  const agent = tx.actor
  const from = c.relay?.from
  const userId = relayPersonOf(tx.state, agent, p, c.decision, from)
  if (agent._tag !== "agent") return
  const person: Actor = { _tag: "user", userId }
  const note = relayNote(tx.state, person, agent, quote, from)
  const relayedBy = {
    agentId: agent.agentId,
    runId: agent.runId,
    ...(from ? { evidence: { agentId: from.agentId, messageId: from.messageId } } : {}),
  }
  const child = new Tx(tx.state, tx.ctx)
  child.scope = { ...tx.scope, actor: person }
  decideAs(child, c, p)
  for (let i = 0; i < child.events.length; i++) {
    const e = child.events[i] as ProjectEvent
    const mine = DECISION_TAGS.has(e._tag) && (e as { proposalId?: string }).proposalId === p.id
    if (!mine) {
      tx.emit(e, child.annotations[i])
      continue
    }
    const relayed: ProjectEvent =
      e._tag === "ProposalRejected"
        ? { ...e, reason: e.reason ? `${e.reason} (${note})` : note, relayedBy }
        : e._tag === "ProposalApproved" || e._tag === "ProposalAnswered"
          ? { ...e, note: e.note ? `${note}\n\n${e.note}` : note, relayedBy }
          : e
    tx.emit(relayed, child.annotations[i])
  }
}

export const decideProposal = (tx: Tx, c: Cmd<"DecideProposal">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  const stale = () =>
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  if (p.state !== "open") stale()
  if (c.revision !== p.revision || c.contentHash !== p.contentHash) stale()
  if (c.relay) {
    relayDecision(tx, c, p, c.relay.quote)
    return
  }
  if (tx.actor._tag === "agent" && p.approver._tag === "agent") {
    agentDecision(tx, c, p)
    return
  }
  decideAs(tx, c, p)
}

/**
 * Decision 0102: what keeps the agent a card is addressed to from deciding it this way, or null. It may decide only
 * what it could do itself: a plan card's (selected) changes must each be allowed by its own policy with no rule that
 * reserves them for a person; a question's chosen option may only carry effects its own policy and level allow.
 * Rejecting, sending back and a free-text answer decide nothing beyond the card.
 */
const agentAuthorityProblem = (tx: Tx, p: Proposal, d: Cmd<"DecideProposal">["decision"]): string | null => {
  const body = p.body
  if (d._tag === "approve" && body._tag === "plan_change") {
    const closure = d.selection
      ? dependencyClosure(body.changes, d.selection)
      : { ok: body.changes.map((_, i) => i) }
    if ("missing" in closure) return null // decide reports the selection itself
    return outsideAuthority(
      tx,
      closure.ok.map((i) => must(body.changes[i], "change")),
    )
  }
  if (d._tag === "answer" && body._tag === "question" && d.optionKey !== null) {
    const opt = body.options.find((o) => o.key === d.optionKey)
    if (!opt) return null // decide reports the unknown option
    const graph = graphEffectsOf(opt)
    const outside = graph.length > 0 ? outsideAuthority(tx, graph) : null
    if (outside) return outside
    for (const e of opt.effects) {
      if (!FLOW_TAGS.has(e._tag)) continue
      const denied = authorize(tx.state, tx.actor, e as unknown as Command)
      if (denied) return `${e._tag}: ${denied._tag === "Forbidden" ? denied.detail : denied._tag}`
    }
  }
  return null
}

/**
 * Decision 0102: the agent a card is addressed to decides it, as itself (the event's actor is the agent; the proposer is
 * woken as for a person's decision). Only within its authority (`agentAuthorityProblem`), else it is refused with the
 * way out: escalate. Its `note` (why) is recorded on the decision: the resolution's note, a reject's reason.
 */
const agentDecision = (tx: Tx, c: Cmd<"DecideProposal">, p: Proposal): void => {
  const denied = mayDecide(tx.state, tx.actor, p, c.decision._tag)
  if (denied) reject(denied)
  if (
    p.body._tag === "memory" &&
    isPersonOnlyCard(p, tx.state) &&
    duplicateNamed(tx.state, p, c.note ?? "") === null
  )
    forbidden(
      "actor_kind",
      `${p.ref} is a person's decision: you may only drop it as a duplicate (name the memory card or memory it repeats, P-n or M-n, in \`note\`); otherwise escalate_card it with your recommendation`,
    )
  // decision 0134: deciding the team's memory takes the deciding agent's curate_memory, as for the lead's own cards
  if (p.body._tag === "memory" && c.decision._tag === "approve") {
    const agent = tx.actor._tag === "agent" ? agentOf(tx.state, tx.actor.agentId) : undefined
    const scope = p.body.draft?.scope ?? p.body.fromScope
    const areaId = scope?._tag === "area" ? scope.areaId : null
    if (!agent || capabilityVerdict(tx.state, agent, "curate_memory", areaId) !== "allow")
      forbidden(
        "policy_never",
        `${p.ref} is outside your authority (curate_memory): escalate_card it with your recommendation instead`,
      )
  }
  const why = agentAuthorityProblem(tx, p, c.decision)
  if (why)
    forbidden(
      "policy_never",
      `${p.ref} is outside your authority (${why}): escalate_card it with your recommendation instead`,
    )
  const note = c.note?.trim() || null
  const child = new Tx(tx.state, tx.ctx)
  child.scope = { ...tx.scope }
  decideAs(child, c, p)
  for (let i = 0; i < child.events.length; i++) {
    const e = child.events[i] as ProjectEvent
    const mine = DECISION_TAGS.has(e._tag) && (e as { proposalId?: string }).proposalId === p.id
    const noted: ProjectEvent =
      !mine || note === null
        ? e
        : e._tag === "ProposalRejected"
          ? { ...e, reason: e.reason && e.reason !== note ? `${e.reason} (${note})` : note }
          : e._tag === "ProposalApproved" || e._tag === "ProposalAnswered"
            ? { ...e, note: e.note ? `${note}\n\n${e.note}` : note }
            : e
    tx.emit(noted, child.annotations[i])
  }
}

/**
 * Decision 0102: the agent a card is addressed to passes it one level up with its note (its recommendation): to the
 * project lead, or from her to the person (`escalateTo`), whose Needs you shows it from then on.
 */
export const escalateProposal = (tx: Tx, c: Cmd<"EscalateProposal">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  if (p.state !== "open" || c.revision !== p.revision)
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  const actor = tx.actor
  if (actor._tag !== "agent") forbidden("actor_kind", "only the agent a card is addressed to escalates it")
  if (p.approver._tag !== "agent" || p.approver.agentId !== actor.agentId)
    forbidden(
      "not_owner",
      p.approver._tag === "agent"
        ? `${p.ref} is addressed to ${agentOf(tx.state, p.approver.agentId)?.name ?? "another agent"}, not you`
        : `${p.ref} is not addressed to you`,
    )
  if ((p.escalations?.length ?? 0) >= 10) invalid(`${p.ref} went up the chain too often`, "proposalId")
  const next = nextAddressee(tx.state, actor.agentId)
  const to: Proposal["approver"] = next
    ? { _tag: "agent", agentId: next, escalateTo: p.approver.escalateTo }
    : { _tag: "human", userId: p.approver.escalateTo }
  tx.emit({
    _tag: "ProposalEscalated",
    proposalId: p.id,
    revision: p.revision,
    by: actor.agentId,
    to,
    note: c.note.trim(),
  })
}

/** The decision itself, as `tx.actor` (a person, or the lead on a card it approves; decision 0093: a relay's person). */
const decideAs = (tx: Tx, c: Cmd<"DecideProposal">, p: Proposal): void => {
  const d = c.decision
  const denied = mayDecide(tx.state, tx.actor, p, d._tag)
  if (denied) reject(denied)
  const body = p.body
  switch (d._tag) {
    case "reject":
      tx.emit({
        _tag: "ProposalRejected",
        proposalId: p.id,
        revision: p.revision,
        reason: d.reason ?? null,
        ...decidedFor(tx.state, p, tx.actor),
      })
      if (body._tag === "outward_action") {
        // a rejected outward action counts like changes_requested on its publish item (§10.10)
        const t = itemOf(tx.state, body.itemId)
        if (t?.status === "in_review") failStage(tx, t.id, "changes_requested", null)
      }
      return
    case "request_revision":
      if (body._tag === "permission") invalid("a permission prompt is approved or denied", "decision")
      tx.emit({ _tag: "ProposalRevisionRequested", proposalId: p.id, revision: p.revision, note: d.note })
      return
    case "answer":
      if (body._tag !== "question") invalid("only questions are answered", "decision")
      // decision 0102: the agent a question is addressed to answers it too (mayDecide checked the address)
      if (!isHuman(tx.actor) && p.approver._tag !== "agent")
        forbidden("actor_kind", "questions are answered by people")
      answerQuestion(tx, p, body, d.optionKey, d.text)
      return
    case "adopt": {
      if (body._tag !== "plan_change" || p.approver._tag !== "lead")
        invalid("only Mayor suggestions are adopted", "decision")
      if (tx.actor._tag !== "agent" || leadOf(tx.state)?.id !== tx.actor.agentId)
        forbidden("not_owner", "only the lead adopts")
      const changesetId = deriveId<ChangesetId>("chg", tx.now, `adopt:${p.id}`)
      tx.emit({ _tag: "ProposalAdopted", proposalId: p.id, changesetId })
      // re-filed as the lead's own changeset, under the lead's policy (anything it may only propose still ends with a person)
      submitChangeset(tx, {
        changesetId,
        rationale: body.rationale,
        changes: body.changes,
        onPropose: "apply_allowed",
      })
      return
    }
    case "approve":
      approve(tx, p, d.selection, d.remember, d.answers)
      return
    case "approve_edited":
      approveEdited(tx, p, d.body)
      return
  }
}

/** Expiry counts as denied; a question with a default applies that option's effects (as the expirer: flow only). */
export const expireProposal = (tx: Tx, c: Cmd<"ExpireProposal">): void => {
  const p = proposalOf(tx.state, c.proposalId) ?? notFound({ _tag: "proposal", id: c.proposalId })
  if (p.state !== "open" && p.state !== "revision_requested")
    reject({ _tag: "StaleProposal", proposalId: p.id, currentRevision: p.revision, state: p.state })
  if (p.expiresAt === null || p.expiresAt > tx.now) invalid("the proposal has not expired yet", "proposalId")
  const body = p.body
  const def =
    body._tag === "question" && body.defaultOnExpiry
      ? body.options.find((o) => o.key === body.defaultOnExpiry)
      : undefined
  if (!def) {
    tx.emit({ _tag: "ProposalExpired", proposalId: p.id, revision: p.revision, appliedDefault: null })
    return
  }
  const failed = attempt(tx, (child) => {
    child.emit({ _tag: "ProposalExpired", proposalId: p.id, revision: p.revision, appliedDefault: def.key })
    applyEffects(child, def.effects, tx.actor, p, `default:${def.key}`)
  })
  if (failed)
    tx.emit({ _tag: "ProposalExpired", proposalId: p.id, revision: p.revision, appliedDefault: null })
}
